// Neutralise la race de démontage des portails Radix à la SOURCE.
//
// Symptôme utilisateur : la modale « Ajouter des comptes » se ferme toute seule
// en pleine saisie, l'utilisateur perd tout et abandonne l'inscription.
//
// Mécanique exacte (confirmée par la télémétrie : le crash suit TOUJOURS un
// `change` sur un Select, jamais une suppression de ligne) :
//   1. L'utilisateur choisit une valeur dans un Select.
//   2. Radix ferme le Select, qui joue une animation de sortie
//      (`data-[state=closed]:animate-out`, `fade-out-0`, `zoom-out-95` dans
//      ui/select.tsx). La suppression du nœud est donc ASYNCHRONE.
//   3. React commit la mise à jour d'état déclenchée par le même `change` et
//      veut retirer des nœuds du même conteneur de portails.
//   4. Les deux acteurs se disputent les mêmes nœuds : `removeChild` reçoit un
//      nœud qui n'est plus son enfant et lève `NotFoundError`.
//
// Pourquoi un error boundary ne suffit pas : l'exception survient pendant la
// COMMIT PHASE. React 19 abandonne alors la racine et remonte tout le sous-arbre
// — `getDerivedStateFromError` peut masquer l'écran d'erreur, mais la modale est
// déjà démontée. Il faut donc empêcher le throw, pas le rattraper.
//
// Le correctif : rendre `removeChild` / `insertBefore` tolérants sur les
// conteneurs de portails uniquement. Si le nœud n'est plus (ou pas) un enfant,
// l'opération est un no-op au lieu de lever. Le DOM finit dans l'état voulu de
// toute façon — l'autre acteur a déjà fait le travail.
//
// Portée volontairement étroite : on ne patche QUE les nœuds portant le flag
// ci-dessous, jamais `Node.prototype` globalement. Une vraie erreur de structure
// DOM ailleurs dans l'app continue de lever normalement.

/** Marque un conteneur comme tolérant aux retraits concurrents. */
const GUARDED = Symbol.for("edgely.portalNodeGuard");

type GuardedNode = Node & { [GUARDED]?: boolean };

/**
 * Rend `removeChild` et `insertBefore` non-levants sur ce nœud précis.
 *
 * Idempotent : ré-appeler sur un conteneur déjà protégé ne fait rien.
 */
export function guardPortalNode(node: Node | null | undefined): void {
  if (!node) return;

  const target = node as GuardedNode;
  if (target[GUARDED]) return;

  const originalRemoveChild = target.removeChild.bind(target);
  const originalInsertBefore = target.insertBefore.bind(target);

  Object.defineProperties(target, {
    removeChild: {
      configurable: true,
      writable: true,
      value: function removeChildSafe<T extends Node>(child: T): T {
        // `parentNode !== this` couvre le nœud déjà retiré par l'animation de
        // sortie de Radix. On rend `child` pour respecter la signature DOM.
        if (child?.parentNode !== target) return child;
        return originalRemoveChild(child) as T;
      },
    },
    insertBefore: {
      configurable: true,
      writable: true,
      value: function insertBeforeSafe<T extends Node>(
        newNode: T,
        referenceNode: Node | null,
      ): T {
        // Même race vue de l'autre côté : la référence a déjà été détachée par
        // l'autre acteur. Insérer en fin de conteneur est le comportement natif
        // quand la référence est `null`, et c'est la position que React
        // recalculera au prochain commit.
        if (referenceNode && referenceNode.parentNode !== target) {
          return originalInsertBefore(newNode, null) as T;
        }
        return originalInsertBefore(newNode, referenceNode) as T;
      },
    },
  });

  target[GUARDED] = true;
}

/** Tests uniquement : indique si le nœud est déjà protégé. */
export function isPortalNodeGuarded(node: Node | null | undefined): boolean {
  return Boolean(node && (node as GuardedNode)[GUARDED]);
}
